/**
 * JixxeoTV — Netflix-style legal live TV inside Jixxeo Apps.
 * Player: hls.js
 * Cast: Remote Playback API / Presentation Request (Chromecast on Chrome desktop + Android)
 * Curated legal streams + user "Add channel" flow with authorization disclaimer.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Tv2, Heart, Search, Plus, X, Star, Circle, Loader2, ExternalLink,
  Settings, ArrowRight, Play, Trash2, ShieldAlert, ChevronLeft, ChevronRight, LogIn,
  Users, Send, Copy, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  tvList, tvCreate, tvFavorites, tvToggleFavorite, tvHistory,
  tvContinueWatching, tvRecordHistory, tvSettings, tvSetWatermark, tvDelete,
  tvPartyCreate, tvPartyGet,
  formatApiError,
} from "@/lib/api";
import TvPlayer from "./_tvPlayer";
import { useWatchParty } from "./_watchParty";

const CORNERS = [
  { id: "top-left", label: "TL" }, { id: "top-right", label: "TR" },
  { id: "bottom-left", label: "BL" }, { id: "bottom-right", label: "BR" },
];

export default function JixxeoTV() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ channels: [], categories: [], countries: [], languages: [] });
  const [active, setActive] = useState(null);
  const [favIds, setFavIds] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [watermark, setWatermark] = useState({ enabled: true, opacity: 0.35, size: 88, corner: "top-right" });

  // ----- Watch Party -----
  const initialParty = useMemo(() => new URLSearchParams(window.location.search).get("party"), []);
  const [partyId, setPartyId] = useState(initialParty);
  const [partyPanelOpen, setPartyPanelOpen] = useState(!!initialParty);
  const [partyShareUrl, setPartyShareUrl] = useState(initialParty ? `${window.location.origin}/embed/jixxeotv?party=${initialParty}` : "");
  const party = useWatchParty({
    partyId, enabled: !!partyId,
    onChannelChange: (ch) => setActive(ch),
  });

  useEffect(() => { loadAll(); tvSettings().then((s) => setWatermark(s.watermark)).catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => { if (user) { loadFavs(); loadHistory(); } }, [user?.id]); // eslint-disable-line

  const loadAll = () => tvList().then(setData).catch(() => {});
  const loadFavs = () => tvFavorites().then((r) => {
    setFavorites(r.channels || []);
    setFavIds(new Set((r.channels || []).map((c) => c.id)));
  }).catch(() => {});
  const loadHistory = () => tvHistory().then((r) => setHistory(r.items || [])).catch(() => {});

  // Filtered channels for search
  const filtered = useMemo(() => {
    if (!q.trim()) return data.channels;
    const rx = new RegExp(q.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
    return data.channels.filter((c) =>
      rx.test(c.name) || rx.test(c.description || "") || (c.tags || []).some((t) => rx.test(t))
    );
  }, [q, data.channels]);

  const rowsByCategory = useMemo(() => {
    const map = new Map();
    filtered.forEach((c) => {
      if (!map.has(c.category)) map.set(c.category, []);
      map.get(c.category).push(c);
    });
    return Array.from(map.entries()).map(([cat, arr]) => ({ category: cat, channels: arr }));
  }, [filtered]);

  const featured = useMemo(() =>
    filtered.filter((c) => c.is_featured).slice(0, 6),
  [filtered]);

  const playChannel = (c) => {
    // If we are the host of a watch party, notify the room to sync everyone
    if (partyId && party.isHost && c?.id) {
      party.changeChannel(c.id);
    } else if (partyId && !party.isHost) {
      toast.info("Only the host can change the channel");
      return;
    }
    setActive(c);
    if (user) { tvRecordHistory(c.id, 0).catch(() => {}); loadHistory(); }
    setTimeout(() => document.getElementById("tv-player-wrap")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  // Bootstrap party channel + sync active state when joining a room
  useEffect(() => {
    if (!partyId) return;
    tvPartyGet(partyId).then((snap) => {
      if (snap.channel_id) {
        const ch = data.channels.find((c) => c.id === snap.channel_id);
        if (ch) setActive(ch);
        else {
          // Might be a user-added channel not in seed; fetch directly
          import("@/lib/api").then(({ tvGet }) => tvGet(snap.channel_id).then((r) => setActive(r.channel)).catch(() => {}));
        }
      }
    }).catch(() => toast.error("Party not found or expired"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, data.channels.length]);

  const startParty = async () => {
    if (!user) { toast.error("Log in to start a watch party"); return; }
    try {
      const res = await tvPartyCreate(active?.id);
      setPartyId(res.party_id);
      const url = `${window.location.origin}${res.share_url}`;
      setPartyShareUrl(url);
      setPartyPanelOpen(true);
      window.history.replaceState(null, "", `/embed/jixxeotv?party=${res.party_id}`);
      try { await navigator.clipboard.writeText(url); toast.success("Party started · link copied"); }
      catch { toast.success("Party started"); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const leaveParty = () => {
    party.leave();
    setPartyId(null);
    setPartyPanelOpen(false);
    setPartyShareUrl("");
    window.history.replaceState(null, "", `/embed/jixxeotv`);
    toast.success("Left the watch party");
  };

  const copyPartyLink = async () => {
    if (!partyShareUrl) return;
    try { await navigator.clipboard.writeText(partyShareUrl); toast.success("Party link copied"); }
    catch { toast.error("Clipboard blocked"); }
  };

  const onProgress = (sec) => { if (active && user) tvRecordHistory(active.id, sec).catch(() => {}); };

  const toggleFav = async (c) => {
    if (!user) { toast.error("Log in to favorite channels"); return; }
    try {
      const r = await tvToggleFavorite(c.id);
      setFavIds((s) => {
        const n = new Set(s);
        if (r.favorited) n.add(c.id); else n.delete(c.id);
        return n;
      });
      loadFavs();
      if (r.favorited) toast.success(`♥ ${c.name}`);
    } catch { toast.error("Couldn't update favorite"); }
  };

  const removeChannel = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try {
      await tvDelete(c.id);
      toast.success("Removed");
      if (active?.id === c.id) setActive(null);
      loadAll(); loadFavs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Resume continue-watching on first login
  useEffect(() => {
    if (!user || active) return;
    tvContinueWatching().then((r) => { if (r.channel) setActive(r.channel); }).catch(() => {});
  }, [user?.id]); // eslint-disable-line

  return (
    <div data-testid="jixxeotv" className="min-h-screen bg-[#020617] text-white pb-16 overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-2 px-3 md:px-8 py-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.5)] shrink-0">
            <Tv2 className="w-5 h-5 text-black" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="mono text-[9px] uppercase tracking-[0.25em] text-cyan-400">// jixxeo apps · live tv</div>
            <div className="font-heading font-black text-lg leading-none uppercase tracking-tighter">JixxeoTV</div>
          </div>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <div className="relative order-3 w-full sm:order-none sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                data-testid="tv-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search channels…"
                className="pl-9 pr-3 py-2 w-full sm:w-56 md:w-64 bg-white/5 border border-white/10 rounded-full text-sm focus:border-cyan-400/60 focus:outline-none" />
            </div>
            {partyId ? (
              <button data-testid="tv-party-panel-btn" onClick={() => setPartyPanelOpen((o) => !o)}
                className={`h-9 px-3 rounded-full font-black mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 shrink-0 ${
                  party.status === "open" ? "bg-[#39FF14] text-black" : "bg-white/10 text-white/70"
                }`}>
                <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Party · </span>{party.viewerCount || 0}
              </button>
            ) : (
              <button data-testid="tv-start-party-btn" onClick={startParty}
                className="h-9 px-3 rounded-full bg-[#39FF14] hover:bg-white text-black font-black mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 shrink-0">
                <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Watch </span>Party
              </button>
            )}
            <button data-testid="tv-add-btn" onClick={() => setShowAdd(true)}
              className="h-9 px-3 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black font-black mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 shrink-0">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
            {isAdmin && (
              <button data-testid="tv-settings-btn" onClick={() => setShowSettings(true)}
                className="h-9 w-9 sm:w-auto sm:px-3 rounded-full border border-white/10 hover:bg-white/5 text-white flex items-center justify-center sm:gap-1.5 mono text-[10px] uppercase tracking-widest shrink-0">
                <Settings className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Admin</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Hero */}
        {featured[0] && !active && (
          <HeroBanner channel={featured[0]} onPlay={() => playChannel(featured[0])} />
        )}

        {/* Player */}
        {active && (
          <div id="tv-player-wrap" className="mt-6">
            <TvPlayer channel={active} watermark={watermark} onProgress={onProgress}
              partyBadge={partyId ? `PARTY · ${party.viewerCount || 1}` : null} />
            <div className="mt-3 flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="mono text-[9px] uppercase tracking-widest text-cyan-400">// {active.category}</span>
                  {active.live && <BadgeChip color="#FF003C">LIVE</BadgeChip>}
                  {active.hd && <BadgeChip color="#39FF14">HD</BadgeChip>}
                  {active.fourk && <BadgeChip color="#FFE300">4K</BadgeChip>}
                  <span className="mono text-[9px] uppercase tracking-widest text-white/40">· {active.country} · {active.language}</span>
                </div>
                <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tighter mt-1">{active.name}</h1>
                {active.description && <div className="text-sm text-white/60 mt-1 max-w-2xl">{active.description}</div>}
                <div className="mono text-[10px] uppercase tracking-widest text-white/40 mt-2">
                  license · {active.license_note || "unspecified"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="tv-fav-active" onClick={() => toggleFav(active)}
                  className={`h-10 px-4 rounded-full border font-bold mono text-xs uppercase tracking-widest flex items-center gap-2 ${
                    favIds.has(active.id) ? "border-[#FF003C] bg-[#FF003C]/10 text-[#FF003C]" : "border-white/15 hover:bg-white/5 text-white/70"
                  }`}>
                  <Heart className={`w-4 h-4 ${favIds.has(active.id) ? "fill-current" : ""}`} />
                  {favIds.has(active.id) ? "Favorited" : "Favorite"}
                </button>
                <button data-testid="tv-share-btn" onClick={async () => {
                  const url = `${window.location.origin}/embed/jixxeotv`;
                  const text = `Watch "${active.name}" on JixxeoTV — ${active.description || active.category}`;
                  try {
                    if (navigator.share) await navigator.share({ title: active.name, text, url });
                    else { await navigator.clipboard.writeText(`${text}\n${url}`); toast.success("Copied to clipboard"); }
                  } catch (e) { if (e?.name !== "AbortError") toast.error("Share cancelled"); }
                }}
                  className="h-10 px-4 rounded-full border border-white/15 hover:bg-white/5 text-white/70 hover:text-white flex items-center gap-2 mono text-xs uppercase tracking-widest">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  Share
                </button>
                {(isAdmin || active.added_by === user?.id) && (
                  <button onClick={() => removeChannel(active)}
                    className="h-10 px-4 rounded-full border border-white/15 hover:bg-white/5 text-white/60 flex items-center gap-1.5 mono text-xs uppercase tracking-widest">
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Continue watching */}
        {history.length > 0 && !q && (
          <ChannelRow title="Continue watching" testId="tv-row-recent"
            channels={history.map((h) => h.channel).filter(Boolean)}
            onPlay={playChannel} favIds={favIds} onFav={toggleFav} />
        )}
        {/* Favorites */}
        {favorites.length > 0 && !q && (
          <ChannelRow title="Your favorites" testId="tv-row-favorites"
            channels={favorites} onPlay={playChannel} favIds={favIds} onFav={toggleFav} accent="#FF003C" />
        )}

        {/* Category rows */}
        {rowsByCategory.map(({ category, channels }) => (
          <ChannelRow key={category} title={category} testId={`tv-row-${category.toLowerCase().replace(/\s+/g,'-')}`}
            channels={channels} onPlay={playChannel} favIds={favIds} onFav={toggleFav}
            onRemove={(c) => (isAdmin || c.added_by === user?.id) ? removeChannel(c) : null}
            isAdmin={isAdmin} userId={user?.id} />
        ))}

        {filtered.length === 0 && (
          <div className="mt-10 p-10 border border-dashed border-white/10 rounded-2xl text-center text-white/50 mono text-[10px] uppercase tracking-widest">
            // no channels match "{q}"
          </div>
        )}

        {/* Legal footer */}
        <div className="mt-16 p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-cyan-300 mb-1">
            <ShieldAlert className="w-3.5 h-3.5" /> // legal
          </div>
          <div className="text-xs text-white/60 leading-relaxed">
            JixxeoTV plays only streams that are properly licensed, in the public domain, published by their
            broadcasters for free distribution, or Creative-Commons-licensed. Every seeded channel notes its
            source. If you add your own channel, you attest that you have the right to view it — Jixxeo does
            not host, cache or rebroadcast copyrighted material.
          </div>
        </div>
      </div>

      {/* Add channel modal */}
      {showAdd && <AddChannelModal onClose={() => setShowAdd(false)} onCreated={(c) => { loadAll(); setShowAdd(false); playChannel(c); }} />}

      {/* Admin settings modal */}
      {showSettings && isAdmin && (
        <WatermarkModal watermark={watermark} onSave={async (wm) => {
          try { const r = await tvSetWatermark(wm); setWatermark(r.watermark); toast.success("Watermark updated"); setShowSettings(false); }
          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }} onClose={() => setShowSettings(false)} />
      )}

      {/* Watch Party sidebar */}
      {partyId && partyPanelOpen && (
        <WatchPartyPanel
          user={user} partyShareUrl={partyShareUrl} party={party}
          onCopyLink={copyPartyLink} onLeave={leaveParty}
          onClose={() => setPartyPanelOpen(false)} />
      )}
    </div>
  );
}

/* -----------------------------------------------------------
   Sub-components
   ----------------------------------------------------------- */

function HeroBanner({ channel, onPlay }) {
  const [logoOk, setLogoOk] = useState(!!channel.logo_url);
  return (
    <div data-testid="tv-hero" className="mt-6 relative overflow-hidden rounded-3xl border border-cyan-500/30 bg-gradient-to-br from-cyan-900/40 via-blue-950/60 to-black h-64 md:h-80 flex items-end p-6 md:p-10">
      <div className="absolute inset-0 opacity-30" style={{
        background: `radial-gradient(circle at 80% 30%, rgba(6,182,212,0.4), transparent 55%), radial-gradient(circle at 15% 80%, rgba(59,130,246,0.3), transparent 60%)`,
      }} />
      <div className="relative z-10 max-w-xl">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 mb-2">// featured now</div>
        <h2 className="font-heading font-black text-3xl md:text-5xl uppercase tracking-tighter leading-none">{channel.name}</h2>
        <div className="text-white/70 mt-2 max-w-md text-sm">{channel.description}</div>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={onPlay} data-testid="tv-hero-play"
            className="inline-flex items-center gap-2 bg-cyan-400 hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-6 py-3 rounded-full shadow-[0_10px_30px_rgba(6,182,212,0.4)]">
            <Play className="w-4 h-4 fill-current" /> Watch now
          </button>
          {channel.live && <span className="mono text-[10px] uppercase tracking-widest text-[#FF003C] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#FF003C] animate-pulse" /> LIVE</span>}
        </div>
      </div>
      {channel.logo_url && logoOk ? (
        <img src={channel.logo_url} alt="" onError={() => setLogoOk(false)}
          className="absolute right-6 top-6 w-16 h-16 md:w-24 md:h-24 object-contain opacity-70" />
      ) : (
        <div className="absolute right-6 top-6 w-16 h-16 md:w-24 md:h-24 rounded-2xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center opacity-70">
          <Tv2 className="w-8 h-8 md:w-12 md:h-12 text-cyan-300" />
        </div>
      )}
    </div>
  );
}

function ChannelRow({ title, testId, channels, onPlay, favIds, onFav, onRemove, accent = "#00F0FF", isAdmin, userId }) {
  const scroller = useRef(null);
  if (!channels?.length) return null;
  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  return (
    <div className="mt-8" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-black uppercase tracking-tighter text-lg" style={{ color: accent }}>
          <span className="mono text-[10px] tracking-widest text-white/40 mr-2">//</span>{title}
        </h3>
        <div className="hidden md:flex gap-1">
          <button onClick={() => scroll(-1)} className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => scroll(1)} className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div ref={scroller} className="flex gap-3 overflow-x-auto scrollbar-none pb-2">
        {channels.map((c) => (
          <ChannelCard key={c.id} channel={c} onPlay={onPlay}
            favorited={favIds?.has(c.id)}
            onFav={() => onFav(c)}
            onRemove={onRemove && (isAdmin || c.added_by === userId) ? () => onRemove(c) : null} />
        ))}
      </div>
    </div>
  );
}

function ChannelCard({ channel, onPlay, favorited, onFav, onRemove }) {
  const [logoOk, setLogoOk] = useState(!!channel.logo_url);
  return (
    <div data-testid={`tv-card-${channel.id}`}
      className="group shrink-0 w-56 md:w-64 rounded-xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-black hover:border-cyan-400/50 transition-all hover:scale-[1.03]">
      <button onClick={() => onPlay(channel)} className="block w-full text-left">
        <div className="aspect-video bg-gradient-to-br from-black to-cyan-950/40 flex items-center justify-center relative overflow-hidden">
          {channel.logo_url && logoOk ? (
            <img src={channel.logo_url} alt="" onError={() => setLogoOk(false)}
              className="max-w-[70%] max-h-[70%] object-contain" loading="lazy" />
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <Tv2 className="w-10 h-10 text-cyan-400/60" />
              <span className="mono text-[9px] uppercase tracking-widest text-white/40 truncate max-w-[80%] px-2 text-center">
                {channel.name}
              </span>
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors">
            <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity fill-current" />
          </div>
          <div className="absolute top-2 left-2 flex gap-1">
            {channel.live && <BadgeChip color="#FF003C" small>LIVE</BadgeChip>}
            {channel.hd && <BadgeChip color="#39FF14" small>HD</BadgeChip>}
            {channel.fourk && <BadgeChip color="#FFE300" small>4K</BadgeChip>}
          </div>
        </div>
        <div className="p-2.5">
          <div className="font-heading font-black text-sm truncate">{channel.name}</div>
          <div className="mono text-[9px] uppercase tracking-widest text-white/40 truncate">{channel.category} · {channel.country}</div>
        </div>
      </button>
      <div className="flex items-center border-t border-white/5">
        <button data-testid={`tv-fav-${channel.id}`} onClick={onFav}
          className={`flex-1 py-2 flex items-center justify-center gap-1 mono text-[10px] uppercase tracking-widest transition-colors ${favorited ? "text-[#FF003C]" : "text-white/50 hover:text-white"}`}>
          <Heart className={`w-3.5 h-3.5 ${favorited ? "fill-current" : ""}`} />
          {favorited ? "Faved" : "Fav"}
        </button>
        {onRemove && (
          <button data-testid={`tv-remove-${channel.id}`} onClick={onRemove}
            className="py-2 px-3 border-l border-white/5 mono text-[10px] uppercase tracking-widest text-white/40 hover:text-[#FF003C]">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function BadgeChip({ children, color, small }) {
  return (
    <span className={`mono uppercase tracking-widest font-bold rounded-full ${small ? "text-[8px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5"}`}
      style={{ background: `${color}30`, color, border: `1px solid ${color}80` }}>
      {children}
    </span>
  );
}

function AddChannelModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", stream_url: "", category: "Public Access", country: "International", language: "English",
    description: "", logo_url: "", hd: false, fourk: false, live: true,
    license_note: "User-provided",
  });
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, message, reason}

  const testUrl = async () => {
    if (!form.stream_url.trim()) { toast.error("Enter a stream URL first"); return; }
    setTesting(true); setTestResult(null);
    try {
      const { tvPreflight } = await import("@/lib/api");
      const res = await tvPreflight(form.stream_url.trim());
      setTestResult(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test failed";
      setTestResult({ ok: false, message: String(msg) });
    } finally { setTesting(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!agreed) { toast.error("Please confirm the authorization disclaimer"); return; }
    if (testResult && testResult.ok === false) {
      if (!window.confirm(`URL test warned: "${testResult.message}"\n\nAdd anyway?`)) return;
    }
    setBusy(true);
    try {
      const r = await tvCreate(form);
      toast.success(`Added ${r.channel.name}`);
      onCreated?.(r.channel);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title="Add a channel" testId="tv-add-modal">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Channel name">
          <input data-testid="tv-add-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2" />
        </Field>
        <Field label="HLS or MP4/M3U8 stream URL">
          <div className="flex gap-2">
            <input data-testid="tv-add-url" required value={form.stream_url}
              onChange={(e) => { setForm((f) => ({ ...f, stream_url: e.target.value })); setTestResult(null); }}
              placeholder="https://example.com/live/stream.m3u8"
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 mono text-xs" />
            <button type="button" data-testid="tv-add-test" onClick={testUrl}
              disabled={testing || !form.stream_url.trim()}
              className="px-3 rounded-lg border border-cyan-400/50 hover:bg-cyan-400/10 mono text-[10px] uppercase tracking-widest text-cyan-300 disabled:opacity-40 flex items-center gap-1">
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Test URL"}
            </button>
          </div>
          {testResult && (
            <div className={`mt-2 rounded-lg p-2 text-xs border ${
              testResult.ok ? "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]"
                            : "border-[#FF003C]/40 bg-[#FF003C]/5 text-[#FF003C]"
            }`}>
              <div className="mono text-[10px] uppercase tracking-widest mb-1">
                {testResult.ok ? "// stream check passed" : "// stream check failed"}
              </div>
              <div>{testResult.message || "Result unclear"}</div>
              {testResult.status && <div className="mono text-[9px] mt-1 opacity-70">HTTP {testResult.status} · {testResult.content_type || "unknown"}</div>}
            </div>
          )}
          <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-1">
            Direct .m3u8 / .mp4 / .mpd / .mp3 only · YouTube/Netflix/etc are not supported
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2" />
          </Field>
          <Field label="Country">
            <input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2" />
          </Field>
        </div>
        <Field label="Logo URL (optional)">
          <input value={form.logo_url} onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))}
            placeholder="https://…/logo.png"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mono text-xs" />
        </Field>
        <Field label="Description (optional)">
          <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={2} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </Field>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest">
            <input type="checkbox" checked={form.hd} onChange={(e) => setForm((f) => ({ ...f, hd: e.target.checked }))} /> HD
          </label>
          <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest">
            <input type="checkbox" checked={form.fourk} onChange={(e) => setForm((f) => ({ ...f, fourk: e.target.checked }))} /> 4K
          </label>
          <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest">
            <input type="checkbox" checked={form.live} onChange={(e) => setForm((f) => ({ ...f, live: e.target.checked }))} /> Live
          </label>
        </div>
        <label className="flex items-start gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 text-xs">
          <input data-testid="tv-add-agree" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" />
          <span>
            I confirm this stream is <strong>lawful</strong> for me to view and, where applicable, is a public,
            CC-licensed, or officially-distributed feed. Jixxeo does not host copyrighted content.
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 mono text-xs uppercase tracking-widest text-white/60">Cancel</button>
          <button data-testid="tv-add-submit" type="submit" disabled={busy || !agreed}
            className="px-5 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black font-black mono text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add channel
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function WatermarkModal({ watermark, onSave, onClose }) {
  const [wm, setWm] = useState(watermark || { enabled: true, opacity: 0.35, size: 88, corner: "top-right" });
  return (
    <ModalShell onClose={onClose} title="JixxeoTV watermark" testId="tv-wm-modal">
      <div className="space-y-4">
        <label className="flex items-center justify-between">
          <span className="mono text-xs uppercase tracking-widest">Enabled</span>
          <input type="checkbox" checked={wm.enabled} onChange={(e) => setWm((w) => ({ ...w, enabled: e.target.checked }))} />
        </label>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest mb-1 text-white/60">Opacity: {(wm.opacity * 100).toFixed(0)}%</div>
          <input type="range" min={0.05} max={1} step={0.05} value={wm.opacity}
            onChange={(e) => setWm((w) => ({ ...w, opacity: Number(e.target.value) }))}
            className="w-full accent-cyan-400" />
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest mb-1 text-white/60">Size: {wm.size}px</div>
          <input type="range" min={40} max={200} step={4} value={wm.size}
            onChange={(e) => setWm((w) => ({ ...w, size: Number(e.target.value) }))}
            className="w-full accent-cyan-400" />
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest mb-1 text-white/60">Corner</div>
          <div className="grid grid-cols-4 gap-2">
            {CORNERS.map((c) => (
              <button key={c.id} data-testid={`tv-wm-corner-${c.id}`} onClick={() => setWm((w) => ({ ...w, corner: c.id }))}
                className={`py-2 rounded-lg mono text-xs uppercase tracking-widest ${
                  wm.corner === c.id ? "bg-cyan-500 text-black font-black" : "border border-white/10 text-white/60"
                }`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 mono text-xs uppercase tracking-widest text-white/60">Cancel</button>
          <button data-testid="tv-wm-save" onClick={() => onSave(wm)}
            className="px-5 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black font-black mono text-xs uppercase tracking-widest">Save</button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid={testId} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[#0b1220] border border-cyan-500/30 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="font-heading font-black uppercase tracking-tighter text-lg">{title}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

/* ============================================================
   Watch Party sidebar — chat + viewers + share link
   ============================================================ */
function WatchPartyPanel({ user, partyShareUrl, party, onCopyLink, onLeave, onClose }) {
  const [msg, setMsg] = useState("");
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [party.chat?.length]);

  const send = (e) => {
    e?.preventDefault();
    if (!msg.trim()) return;
    if (party.sendChat(msg)) setMsg("");
  };

  return (
    <div data-testid="tv-party-sidebar"
      className="fixed inset-y-0 right-0 z-40 w-full sm:w-96 bg-[#050b18] border-l border-[#39FF14]/30 shadow-2xl flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[#39FF14] flex items-center justify-center">
          <Users className="w-4 h-4 text-black" strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="mono text-[9px] uppercase tracking-widest text-[#39FF14]">// watch party · live</div>
          <div className="font-heading font-black text-lg uppercase tracking-tighter">Party of {party.viewerCount || 1}</div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Share link */}
      <div className="p-3 border-b border-white/5 space-y-2">
        <div className="mono text-[9px] uppercase tracking-widest text-white/50">Invite friends</div>
        <div className="flex gap-2">
          <input readOnly value={partyShareUrl} onFocus={(e) => e.target.select()}
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 mono text-[10px] text-white/70" />
          <button data-testid="tv-party-copy" onClick={onCopyLink}
            className="px-3 rounded-lg bg-[#39FF14] hover:bg-white text-black font-bold mono text-[10px] uppercase tracking-widest flex items-center gap-1">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
        </div>
        <div className="mono text-[9px] uppercase tracking-widest text-white/40">
          Share the link · up to 10 viewers · {party.isHost ? "you control the channel" : "host controls the channel"}
        </div>
      </div>

      {/* Viewers */}
      <div className="p-3 border-b border-white/5">
        <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-2">// viewers</div>
        <div className="flex flex-wrap gap-2">
          {(party.viewers || []).map((v) => (
            <div key={v.sid} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/5 border border-white/10 mono text-[10px]">
              <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
              <span className="text-white/80 truncate max-w-[110px]">{v.name}</span>
              {v.is_host && <Crown className="w-3 h-3 text-[#FFE300]" />}
            </div>
          ))}
        </div>
      </div>

      {/* Chat */}
      <div ref={scrollerRef} data-testid="tv-party-chat"
        className="flex-1 overflow-y-auto p-3 space-y-2">
        {(party.chat || []).length === 0 && (
          <div className="text-center mono text-[10px] uppercase tracking-widest text-white/40 py-8">
            // no messages yet
          </div>
        )}
        {(party.chat || []).map((m) => (
          <div key={m.id} className="flex items-start gap-2">
            <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: m.color }} />
            <div className="min-w-0 flex-1">
              <div className="mono text-[9px] uppercase tracking-widest text-white/40">
                {m.name} · {new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="text-sm text-white/90 break-words">{m.text}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <input
          data-testid="tv-party-input"
          value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={280}
          placeholder={user ? "Say something…" : "Log in to chat"}
          disabled={!user}
          className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm focus:border-[#39FF14]/60 focus:outline-none" />
        <button data-testid="tv-party-send" type="submit" disabled={!user || !msg.trim()}
          className="w-10 h-10 rounded-full bg-[#39FF14] hover:bg-white text-black flex items-center justify-center disabled:opacity-40">
          <Send className="w-4 h-4" />
        </button>
      </form>
      <button data-testid="tv-party-leave" onClick={onLeave}
        className="border-t border-white/10 py-2 mono text-[10px] uppercase tracking-widest text-white/50 hover:text-[#FF003C]">
        Leave party
      </button>
    </div>
  );
}
